import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import ProgressIndicator from './components/ProgressIndicator';
import BasicInformationStep from './components/BasicInformationStep';
import MusicalSpecialtiesStep from './components/MusicalSpecialtiesStep';
import DocumentationStep from './components/DocumentationStep';
import ProgramInfoPanel from './components/ProgramInfoPanel';

const PartnerRegistration = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [registrationType, setRegistrationType] = useState('partner');
  const [formData, setFormData] = useState({
    // Basic Information
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    professionalRole: '',
    companyName: '',
    companyType: '',
    siret: '',
    website: '',
    address: '',
    postalCode: '',
    city: '',
    acceptTerms: false,
    acceptMarketing: false,
    acceptDataProcessing: false,
  password: '',
  confirmPassword: '',
    
    // Musical Specialties
    musicalGenres: [],
    experienceLevel: '',
    venueTypes: [],
    equipment: [],
    equipmentDetails: '',
    yearsExperience: '',
    notableWork: '',
    averageAudience: '',
    
    // Documentation
    documents: {},
    accountHolderName: '',
    iban: '',
    bic: '',
    bankName: '',
    certifyInformation: false,
    authorizeVerification: false,
    smsNotifications: false
  });
  
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [language, setLanguage] = useState('fr');

  const stepLabels = [
    'Informations de base',
    'Spécialités musicales',
    'Documentation'
  ];

  const totalSteps = 3;

  useEffect(() => {
    const savedLanguage = localStorage.getItem('language');
    if (savedLanguage) {
      setLanguage(savedLanguage);
    }
    const pendingType = localStorage.getItem('pendingRegistrationType');
    if (pendingType) setRegistrationType(pendingType);
  }, []);

  const validateStep = (step) => {
    const newErrors = {};

    if (step === 1) {
      if (!formData?.firstName?.trim()) newErrors.firstName = 'Le prénom est requis';
      if (!formData?.lastName?.trim()) newErrors.lastName = 'Le nom de famille est requis';
      if (!formData?.email?.trim()) {
        newErrors.email = 'L\'adresse e-mail est requise';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
        newErrors.email = 'Format d\'e-mail invalide';
      }
      if (!formData?.phone?.trim()) newErrors.phone = 'Le numéro de téléphone est requis';
      if (!formData?.professionalRole) newErrors.professionalRole = 'Le rôle professionnel est requis';
      if (!formData?.address?.trim()) newErrors.address = 'L\'adresse est requise';
      if (!formData?.postalCode?.trim()) newErrors.postalCode = 'Le code postal est requis';
      if (!formData?.city?.trim()) newErrors.city = 'La ville est requise';
      if (!formData?.acceptTerms) newErrors.acceptTerms = 'Vous devez accepter les conditions';
      if (!formData?.acceptDataProcessing) newErrors.acceptDataProcessing = 'Vous devez accepter le traitement des données';
  if (!formData?.password || formData?.password?.length < 6) newErrors.password = 'Mot de passe (>=6 caractères) requis';
  if (formData?.password !== formData?.confirmPassword) newErrors.confirmPassword = 'Les mots de passe ne correspondent pas';
    }

    if (step === 2) {
      if (!formData?.musicalGenres?.length) newErrors.musicalGenres = 'Sélectionnez au moins un genre musical';
      if (!formData?.experienceLevel) newErrors.experienceLevel = 'Le niveau d\'expérience est requis';
      if (!formData?.venueTypes?.length) newErrors.venueTypes = 'Sélectionnez au moins un type de lieu';
      if (!formData?.yearsExperience) newErrors.yearsExperience = 'Les années d\'expérience sont requises';
    }

    if (step === 3) {
      if (!formData?.documents?.portfolio?.length) newErrors.portfolio = 'Le portfolio est requis';
      if (!formData?.accountHolderName?.trim()) newErrors.accountHolderName = 'Le nom du titulaire est requis';
      if (!formData?.iban?.trim()) {
        newErrors.iban = 'L\'IBAN est requis';
      } else if (!/^[A-Z]{2}[0-9]{2}[A-Z0-9]{4}[0-9]{7}([A-Z0-9]?){0,16}$/?.test(formData?.iban?.replace(/\s/g, ''))) {
        newErrors.iban = 'Format IBAN invalide';
      }
      if (!formData?.certifyInformation) newErrors.certifyInformation = 'Vous devez certifier les informations';
      if (!formData?.authorizeVerification) newErrors.authorizeVerification = 'Vous devez autoriser la vérification';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      if (currentStep < totalSteps) {
        setCurrentStep(currentStep + 1);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const { signIn } = useAuth();

  const handleSubmit = async () => {
    if (!validateStep(currentStep)) return;

    setIsSubmitting(true);
    
    try {
      // Création utilisateur Supabase avec metadata rôle
  const roleMeta = ['venue','agent','artist','media'].includes(registrationType) ? registrationType : 'partner';
      const fullName = `${formData.firstName} ${formData.lastName}`.trim();
      const { data, error } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.password,
        options: {
          data: {
            full_name: fullName,
            role: roleMeta,
            registration_type: registrationType
          }
        }
      });

      if (error) {
        setErrors({ submit: error.message });
      } else {
        // Auto sign-in (si confirmation email désactivée) sinon message
        if (data?.session) {
          await signIn(formData.email, formData.password);
          navigate('/partner-dashboard', { state: { message: 'Compte créé avec succès.', type: 'success' } });
        } else {
          navigate('/', { state: { message: 'Vérifiez votre e-mail pour confirmer votre compte.', type: 'info' } });
        }
      }
    } catch (error) {
      setErrors({ submit: 'Erreur lors de la soumission. Veuillez réessayer.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <BasicInformationStep
            formData={formData}
            setFormData={setFormData}
            errors={errors}
          />
        );
      case 2:
        return (
          <MusicalSpecialtiesStep
            formData={formData}
            setFormData={setFormData}
            errors={errors}
          />
        );
      case 3:
        return (
          <DocumentationStep
            formData={formData}
            setFormData={setFormData}
            errors={errors}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-surface border-b border-border shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-3">
              <div className="flex items-center justify-center w-8 h-8 bg-accent rounded-lg">
                <Icon name="Music" size={20} color="white" />
              </div>
              <span className="text-xl font-semibold text-foreground">Artis.io</span>
            </div>
            
            <div className="flex items-center space-x-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/login')}
              >
                <Icon name="LogIn" size={16} className="mr-2" />
                Se connecter
              </Button>
            </div>
          </div>
        </div>
      </header>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            <div className="mb-6">
              <h1 className="text-3xl font-bold text-foreground mb-2">
                {registrationType === 'artist' && 'Inscription Artiste'}
                {registrationType === 'venue' && 'Inscription Lieu'}
                {registrationType === 'agent' && 'Inscription Agent'}
                {registrationType === 'partner' && 'Inscription Partenaire'}
              </h1>
              <p className="text-muted-foreground">
                {registrationType === 'artist' && 'Créez votre profil public, présentez votre univers et facilitez les mises en relation.'}
                {registrationType === 'venue' && 'Valorisez votre espace (club, festival, restaurant, événement) et optimisez vos programmations.'}
                {registrationType === 'agent' && 'Centralisez la gestion de vos artistes, échanges et commissions.'}
                {registrationType === 'partner' && 'Rejoignez notre réseau de professionnels et commencez à générer des revenus.'}
              </p>
            </div>

            <ProgressIndicator
              currentStep={currentStep}
              totalSteps={totalSteps}
              stepLabels={stepLabels}
            />

            {/* Step Content */}
            <div className="mb-8">
              {renderStepContent()}
            </div>

            {/* Error Message */}
            {errors?.submit && (
              <div className="mb-6 p-4 bg-error/10 border border-error/20 rounded-lg">
                <div className="flex items-center space-x-2">
                  <Icon name="AlertCircle" size={16} className="text-error" />
                  <span className="text-sm text-error">{errors?.submit}</span>
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex items-center justify-between">
              <Button
                variant="outline"
                onClick={handlePrevious}
                disabled={currentStep === 1}
              >
                <Icon name="ChevronLeft" size={16} className="mr-2" />
                Précédent
              </Button>

              <div className="flex items-center space-x-3">
                {currentStep < totalSteps ? (
                  <Button
                    variant="default"
                    onClick={handleNext}
                    iconName="ChevronRight"
                    iconPosition="right"
                  >
                    Suivant
                  </Button>
                ) : (
                  <Button
                    variant="default"
                    onClick={handleSubmit}
                    loading={isSubmitting}
                    iconName="Check"
                    iconPosition="left"
                  >
                    {isSubmitting ? 'Soumission...' : 'Finaliser l\'inscription'}
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-8">
              <ProgramInfoPanel />
            </div>
          </div>
        </div>
      </div>
      {/* Footer */}
      <footer className="bg-surface border-t border-border mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col md:flex-row items-center justify-between">
            <div className="flex items-center space-x-3 mb-4 md:mb-0">
              <div className="flex items-center justify-center w-6 h-6 bg-accent rounded">
                <Icon name="Music" size={14} color="white" />
              </div>
              <span className="text-sm text-muted-foreground">
                © {new Date()?.getFullYear()} Artis.io. Tous droits réservés.
              </span>
            </div>
            
            <div className="flex items-center space-x-6 text-sm text-muted-foreground">
              <a href="#" className="hover:text-foreground transition-colors">
                Conditions d'utilisation
              </a>
              <a href="#" className="hover:text-foreground transition-colors">
                Politique de confidentialité
              </a>
              <a href="#" className="hover:text-foreground transition-colors">
                Support
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PartnerRegistration;